import type {
  NotificationResponse,
  PendingInvitationResponse,
} from '@stw/types';

interface FormattedNotification {
  icon: string;
  text: string;
  href: string | null;
}

/**
 * Render a Notification row's type + metadata into display text and a
 * deep-link. metadata is a frozen snapshot taken at write time (see
 * backend/src/models/notification.ts), so this never needs to join back to
 * live data — it must still render sensibly for types not wired up yet.
 */
export const formatNotification = (
  n: NotificationResponse
): FormattedNotification => {
  const metadata = (n.metadata ?? {}) as Record<string, any>;
  const itemTitle = metadata.item_title || 'an item';
  const folderTitle = metadata.folder_title || 'a folder';
  const itemHref = metadata.item_id
    ? `/item/${metadata.item_id}`
    : metadata.scene_id
      ? `/scene/${metadata.scene_id}`
      : null;

  switch (n.type) {
    case 'ITEM_BANNED':
      return {
        icon: 'pi pi-ban',
        text: `"${itemTitle}" was banned${metadata.reason ? `: ${metadata.reason}` : ''}`,
        href: itemHref,
      };
    case 'ITEM_UNBANNED':
      return {
        icon: 'pi pi-check-circle',
        text: `"${itemTitle}" was unbanned`,
        href: itemHref,
      };
    case 'ITEM_COMMENT':
      return {
        icon: 'pi pi-comment',
        text: metadata.actor_name
          ? `${metadata.actor_name} commented on "${itemTitle}"`
          : `New comment on "${itemTitle}"`,
        href: itemHref,
      };
    case 'ITEM_SHARE_INVITE_SENT':
      return {
        icon: 'pi pi-send',
        text: `You shared "${itemTitle}"`,
        href: itemHref,
      };
    case 'ITEM_SHARE_INVITE_RECEIVED':
      return {
        icon: 'pi pi-gift',
        text: metadata.actor_name
          ? `${metadata.actor_name} shared "${itemTitle}" with you`
          : `You received a share invite for "${itemTitle}"`,
        href: itemHref,
      };
    case 'ITEM_SHARE_INVITE_ACCEPTED':
      return {
        icon: 'pi pi-check',
        text: metadata.actor_name
          ? `${metadata.actor_name} accepted your share of "${itemTitle}"`
          : `Your share of "${itemTitle}" was accepted`,
        href: itemHref,
      };
    case 'FOLDER_SHARE_INVITE_SENT':
      return {
        icon: 'pi pi-send',
        text: `You shared the folder "${folderTitle}"${metadata.actor_name ? ` with ${metadata.actor_name}` : ''}`,
        href: null,
      };
    case 'FOLDER_SHARE_INVITE_RECEIVED':
      return {
        icon: 'pi pi-folder',
        text: metadata.actor_name
          ? `${metadata.actor_name} shared the folder "${folderTitle}" with you`
          : `You received a share invite for the folder "${folderTitle}"`,
        href: null,
      };
    case 'FOLDER_SHARE_INVITE_ACCEPTED':
      return {
        icon: 'pi pi-check',
        text: metadata.actor_name
          ? `${metadata.actor_name} accepted your folder "${folderTitle}"`
          : `Your folder "${folderTitle}" was accepted`,
        href: null,
      };
    case 'GROUP_INVITE_SENT':
      return {
        icon: 'pi pi-send',
        text: `You invited someone to "${metadata.group_name || 'a group'}"`,
        href: null,
      };
    case 'GROUP_INVITE_RECEIVED':
      return {
        icon: 'pi pi-users',
        text: `You were invited to "${metadata.group_name || 'a group'}"`,
        href: null,
      };
    case 'GROUP_INVITE_ACCEPTED':
      return {
        icon: 'pi pi-check',
        text: `Your group invite to "${metadata.group_name || 'a group'}" was accepted`,
        href: null,
      };
    case 'ITEM_PROCESSING_SUCCESS':
      return {
        icon: 'pi pi-check-circle',
        text: `"${itemTitle}" finished processing`,
        href: itemHref,
      };
    case 'ITEM_PROCESSING_FAILED':
      return {
        icon: 'pi pi-exclamation-triangle',
        text: `"${itemTitle}" failed to process`,
        href: itemHref,
      };
    default:
      return {
        icon: 'pi pi-bell',
        text: n.type,
        href: itemHref,
      };
  }
};

/**
 * What a pending invitation is about, for the bell dropdown / Settings list:
 * "<inviter> {action}" with the shared thing's name under it.
 */
export const formatInvitation = (
  invitation: PendingInvitationResponse
): { action: string; name: string | undefined } => {
  switch (invitation.type) {
    case 'item_share':
      return { action: 'shared a model with you', name: invitation.item?.name };
    case 'folder_share':
      return {
        action: `shared a folder (${invitation.item_ids.length} model${invitation.item_ids.length === 1 ? '' : 's'}) with you`,
        name: invitation.folder?.name,
      };
    default:
      return { action: 'invited you to a group', name: invitation.group?.name };
  }
};
